<script setup lang="ts">
  import {
    ComboboxContent,
    ComboboxPortal,
    ComboboxViewport,
    type ComboboxContentProps,
    injectComboboxRootContext
  } from 'reka-ui'
  import { computed, type HTMLAttributes } from 'vue'
  import { cn } from '../../../lib/utils'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'

  defineOptions({ inheritAttrs: false })

  const props = withDefaults(
    defineProps<
      ComboboxContentProps & {
        class?: HTMLAttributes['class']
      }
    >(),
    {
      position: 'popper',
      sideOffset: 1,
      align: 'start',
      collisionPadding: 8
    }
  )

  const rootContext = injectComboboxRootContext()
  const isOpen = computed(() => rootContext.open.value)
  const { zIndex } = useDialogZIndex(isOpen)
</script>

<template>
  <ComboboxPortal>
    <ComboboxContent
      data-slot="combobox-content"
      v-bind="{ ...props, ...$attrs }"
      :style="{ zIndex }"
      :class="
        cn(
          'bg-popover text-popover-foreground relative max-h-[200px] min-w-[8rem] overflow-hidden rounded-md border shadow-md',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          props.class
        )
      "
    >
      <ComboboxViewport
        :class="
          cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--reka-combobox-content-available-height)] min-w-[var(--reka-combobox-trigger-width)]'
          )
        "
      >
        <slot />
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxPortal>
</template>
